import i18n from "../i18n";
import { api } from "./client";
import { ApiClientError, isApiError, type ApiErrorBody } from "./types";
import { unwrapApiData, unwrapApiList } from "./unwrap";
export { fetchParties } from "./crm";

/**
 * Item type (0-based) — screen taxonomy mapped to backend product_class.
 * 0 تجاري، 1 خدمة، 2 خام، 3 نصف مصنع، 4 تام.
 */
export const ItemType = {
  STORABLE: 0,
  SERVICE: 1,
  CONSUMABLE: 2,
  SEMI_FINISHED: 3,
  FINISHED: 4,
} as const;

export type ItemTypeCode = (typeof ItemType)[keyof typeof ItemType];

/** Backend product.product_class (0-based) — خامات = 0 تُصرف بمتوسط التكلفة. */
export const ProductClass = {
  RAW: 0,
  FINISHED: 1,
  SERVICE: 2,
  TRADING: 3,
  SEMI_FINISHED: 4,
} as const;

export type ProductClassCode = (typeof ProductClass)[keyof typeof ProductClass];

export function isRawMaterialProductClass(productClass: number): boolean {
  return productClass === ProductClass.RAW;
}

/** Every class except service keeps stock and can appear on a movement. */
export function itemTracksStock(itemType: ItemTypeCode): boolean {
  return itemType !== ItemType.SERVICE;
}

interface ProductApiRow {
  productId: string;
  sku: string;
  pieceSku?: string | null;
  modelSku?: string | null;
  nameI18nKey: string;
  productClass: number;
  uomCode: number;
  listPriceMinor: number;
  trackInventory: boolean;
  trackBatch?: boolean;
  trackExpiry?: boolean;
  expiryFromProduction?: boolean;
  shelfLifeDays?: number | null;
  stdPackConv?: number | null;
  convTolerancePct?: number;
  reorderPointScaled?: number | null;
  productionPointScaled?: number | null;
  categoryId?: string | null;
  baseName?: string | null;
  colorId?: string | null;
  sizeId?: string | null;
  verticalAttributes: Record<string, unknown>;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryItem {
  itemId: string;
  sku: string;
  pieceSku: string | null;
  modelSku: string | null;
  name: string;
  productClass: ProductClassCode;
  itemType: ItemTypeCode;
  priceMinor: number;
  costMinor: number;
  trackBatch: boolean;
  trackExpiry: boolean;
  expiryFromProduction: boolean;
  shelfLifeDays: number | null;
  createdAt: string;
  updatedAt: string;
  /** معامل التعبئة الافتراضي للصنف (عدد الوحدات الصغيرة في التعبئة الكبرى). NULL = غير محدد. */
  stdPackConv: number | null;
  /** نسبة تسامح% للانحراف بين (تعبئة×محول) والكمية للمنتجات غير المتماثلة زي القماش. افتراضي 0 = صارم 100%. */
  convTolerancePct: number;
  /** اسم وحدة التعبئة بالعربي (مثال: كرتونة، رول، طبلة). غير موجود قديمًا = NULL. */
  packUnitName?: string | null;
  /** حد الطلب للشراء (خامات/تجاري) — qtyScaled. */
  reorderPointScaled?: number | null;
  /** حد الإنتاج (تام/نصف مصنع) — qtyScaled. */
  productionPointScaled?: number | null;
  /** أقل مجموعة تصنيف (ورقة الشجرة) — المسار الكامل يُحسب من شجرة التصنيف. */
  categoryId: string | null;
  /** اسم الصنف الأساسي بدون لون/مقاس (مثال: فستان كتان). `name` هو الاسم الكامل. */
  baseName: string | null;
  colorId: string | null;
  sizeId: string | null;
  /** صورة المنيو (مطاعم) — من vertical_attributes.menuImageUrl */
  menuImageUrl: string | null;
}

export interface CreateItemPayload {
  /** اسم الصنف الأساسي (يُركَّب مع اللون والمقاس في الاسم الكامل). */
  name: string;
  /** فارغ = يُولّده الخادم (GDO-…). */
  sku?: string;
  categoryId?: string | null;
  colorId?: string | null;
  sizeId?: string | null;
  pieceSku?: string;
  modelSku?: string;
  itemType: ItemTypeCode;
  priceMinor: number;
  costMinor: number;
  verticalCode?: number;
  trackBatch?: boolean;
  trackExpiry?: boolean;
  expiryFromProduction?: boolean;
  shelfLifeDays?: number;
  /** اسم وحدة التعبئة بالعربي اختياري (كرتونة/رول/طبلة/لتر) */
  packUnitName?: string;
  /** المحول الافتراضي من التعبئة للوحدة الصغيرة. NULL = معطل. */
  stdPackConv?: number | null;
  /** نسبة تسامح% للقماش/المعادن (0-25). افتراضي 0 = صارم 100% */
  convTolerancePct?: number;
  /** حد الطلب للشراء — qtyScaled (خامات / تجاري). */
  reorderPointScaled?: number | null;
  /** حد الإنتاج — qtyScaled (تام / نصف مصنع). */
  productionPointScaled?: number | null;
}

function itemTypeToProductClass(itemType: ItemTypeCode): number {
  switch (itemType) {
    case ItemType.SERVICE:
      return ProductClass.SERVICE;
    case ItemType.CONSUMABLE:
      return ProductClass.RAW;
    case ItemType.SEMI_FINISHED:
      return ProductClass.SEMI_FINISHED;
    case ItemType.FINISHED:
      return ProductClass.FINISHED;
    case ItemType.STORABLE:
    default:
      return ProductClass.TRADING;
  }
}

function productClassToItemType(
  productClass: number,
  verticalAttributes?: Record<string, unknown>,
): ItemTypeCode {
  const stored = verticalAttributes?.itemType;
  if (
    typeof stored === "number" &&
    stored >= ItemType.STORABLE &&
    stored <= ItemType.FINISHED
  ) {
    return stored as ItemTypeCode;
  }
  switch (productClass) {
    case ProductClass.SERVICE:
      return ItemType.SERVICE;
    case ProductClass.RAW:
      return ItemType.CONSUMABLE;
    case ProductClass.SEMI_FINISHED:
      return ItemType.SEMI_FINISHED;
    case ProductClass.FINISHED:
      return ItemType.FINISHED;
    default:
      return ItemType.STORABLE;
  }
}

function mapProductToItem(row: ProductApiRow): InventoryItem {
  const attrs = row.verticalAttributes ?? {};
  const displayName =
    typeof attrs.displayName === "string" && attrs.displayName.trim()
      ? attrs.displayName.trim()
      : row.nameI18nKey;
  const costRaw = attrs.defaultCostMinor ?? attrs.costMinor ?? 0;
  const costMinor =
    typeof costRaw === "number" && Number.isInteger(costRaw) ? costRaw : 0;
  const menuImageRaw = attrs.menuImageUrl ?? attrs.menu_image_url;
  const menuImageUrl =
    typeof menuImageRaw === "string" && menuImageRaw.trim() ? menuImageRaw.trim() : null;

  return {
    itemId: row.productId,
    sku: row.sku,
    pieceSku: row.pieceSku?.trim() || null,
    modelSku: row.modelSku?.trim() || null,
    name: displayName,
    productClass: row.productClass as ProductClassCode,
    itemType: productClassToItemType(row.productClass, attrs),
    priceMinor: row.listPriceMinor,
    costMinor,
    trackBatch: row.trackBatch === true,
    trackExpiry: row.trackExpiry === true,
    expiryFromProduction: row.expiryFromProduction === true,
    shelfLifeDays:
      row.shelfLifeDays !== null && row.shelfLifeDays !== undefined
        ? Number(row.shelfLifeDays)
        : null,
    stdPackConv:
      row.stdPackConv !== null && row.stdPackConv !== undefined
        ? Number(row.stdPackConv)
        : null,
    convTolerancePct: Number.isInteger(Number(row.convTolerancePct))
      ? Math.max(0, Math.min(25, Number(row.convTolerancePct)))
      : 0,
    reorderPointScaled: row.reorderPointScaled ?? null,
    productionPointScaled: row.productionPointScaled ?? null,
    categoryId: row.categoryId ?? null,
    baseName: row.baseName?.trim() || null,
    colorId: row.colorId ?? null,
    sizeId: row.sizeId ?? null,
    menuImageUrl,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

export async function fetchItems(): Promise<InventoryItem[]> {
  const res = await api.get<
    { ok: true; data: ProductApiRow[] } | import("./types").ApiErrorBody
  >("/v1/inventory/items");

  const rows = unwrapApiList(res);

  return [...rows]
    .map(mapProductToItem)
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
}

export const PRODUCT_WIPE_CONFIRM_PHRASE = "DELETE ALL PRODUCTS";

export type ProductGridRow = Record<string, string>;

export type ProductBulkGridQuery = {
  headersOnly?: boolean;
  q?: string;
  categoryL1?: string;
  categoryL2?: string;
  categoryL3?: string;
  productClass?: string;
  limit?: number;
};

export async function fetchProductBulkGrid(
  query: ProductBulkGridQuery = {},
): Promise<{
  headers: string[];
  rows: ProductGridRow[];
}> {
  const params = new URLSearchParams();
  if (query.headersOnly) params.set("headersOnly", "1");
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.categoryL1?.trim()) params.set("categoryL1", query.categoryL1.trim());
  if (query.categoryL2?.trim()) params.set("categoryL2", query.categoryL2.trim());
  if (query.categoryL3?.trim()) params.set("categoryL3", query.categoryL3.trim());
  if (query.productClass?.trim()) params.set("productClass", query.productClass.trim());
  if (query.limit != null && query.limit > 0) params.set("limit", String(query.limit));
  const qs = params.toString();
  const res = await api.get<
    | { ok: true; data: { headers: string[]; rows: ProductGridRow[] } }
    | import("./types").ApiErrorBody
  >(`/v1/inventory/items/bulk-grid${qs ? `?${qs}` : ""}`);
  return unwrapApiData(res);
}

export async function saveProductBulkGrid(rows: ProductGridRow[]): Promise<{
  inserted: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}> {
  const res = await api.post<
    | {
        ok: true;
        data: {
          inserted: number;
          updated: number;
          errors: Array<{ row: number; message: string }>;
        };
      }
    | import("./types").ApiErrorBody
  >("/v1/inventory/items/bulk-grid/save", { rows });
  return unwrapApiData(res);
}

export async function reviewProductBulkGridAi(rows: ProductGridRow[]): Promise<{
  warnings: string[];
  aiText: string | null;
  aiAvailable: boolean;
  aiModel: string | null;
}> {
  const res = await api.post<
    | {
        ok: true;
        data: {
          warnings: string[];
          aiText: string | null;
          aiAvailable: boolean;
          aiModel: string | null;
        };
      }
    | import("./types").ApiErrorBody
  >("/v1/inventory/items/bulk-grid/ai-review", { rows });
  return unwrapApiData(res);
}

export type ProductBulkDeleteBlocked = {
  productId: string;
  sku: string;
  reason: string;
};

export async function deleteProductsBulk(productIds: string[]): Promise<{
  deleted: number;
  blocked: ProductBulkDeleteBlocked[];
}> {
  const res = await api.post<
    | {
        ok: true;
        data: { deleted: number; blocked: ProductBulkDeleteBlocked[] };
      }
    | import("./types").ApiErrorBody
  >("/v1/inventory/items/bulk-delete", { productIds });
  return unwrapApiData(res);
}

export async function wipeProductCatalog(confirmation: string): Promise<{
  deletedProducts: number;
  deletedStockLayers: number;
  deletedMovements: number;
  deletedBomHeaders: number;
}> {
  const res = await api.post<
    | {
        ok: true;
        data: {
          deletedProducts: number;
          deletedStockLayers: number;
          deletedMovements: number;
          deletedBomHeaders: number;
        };
      }
    | import("./types").ApiErrorBody
  >("/v1/inventory/items/wipe-catalog", { confirmation });
  return unwrapApiData(res);
}

export async function createItem(payload: CreateItemPayload): Promise<InventoryItem> {
  const name = payload.name.trim();
  const sku = payload.sku?.trim() ?? "";
  if (!name) {
    throw new Error("name is required");
  }

  if (!Number.isInteger(payload.priceMinor) || payload.priceMinor < 0) {
    throw new Error("priceMinor must be a non-negative integer");
  }
  if (!Number.isInteger(payload.costMinor) || payload.costMinor < 0) {
    throw new Error("costMinor must be a non-negative integer");
  }

  const priceMinor = payload.priceMinor;
  const costMinor = payload.costMinor;

  const itemType = payload.itemType;
  const trackInventory = itemType !== ItemType.SERVICE;

  const convTolerancePct = Number.isInteger(payload.convTolerancePct)
    ? Math.max(0, Math.min(25, payload.convTolerancePct!))
    : 0;
  const stdPackConv =
    payload.stdPackConv !== undefined && payload.stdPackConv !== null
      ? Number(payload.stdPackConv)
      : undefined;
  const packUnitName =
    typeof payload.packUnitName === "string" && payload.packUnitName.trim()
      ? payload.packUnitName.trim()
      : undefined;

  const res = await api.post<
    { ok: true; data: ProductApiRow } | import("./types").ApiErrorBody
  >("/v1/inventory/items", {
    ...(sku ? { sku } : {}),
    pieceSku: payload.pieceSku?.trim() || undefined,
    modelSku: payload.modelSku?.trim() || undefined,
    nameI18nKey: name,
    categoryId: payload.categoryId || undefined,
    colorId: payload.colorId || undefined,
    sizeId: payload.sizeId || undefined,
    baseName: payload.categoryId || payload.colorId || payload.sizeId ? name : undefined,
    productClass: itemTypeToProductClass(itemType),
    listPriceMinor: priceMinor,
    trackInventory,
    trackBatch: payload.trackBatch === true,
    trackExpiry: payload.trackExpiry === true,
    expiryFromProduction: payload.expiryFromProduction === true,
    shelfLifeDays: payload.shelfLifeDays,
    verticalCode: payload.verticalCode,
    stdPackConv,
    convTolerancePct,
    packUnitName,
    reorderPointScaled: payload.reorderPointScaled ?? undefined,
    productionPointScaled: payload.productionPointScaled ?? undefined,
    verticalAttributes: {
      itemType,
      defaultCostMinor: costMinor,
      displayName: name,
      ...(packUnitName ? { packUnitName } : {}),
    },
  });

  return mapProductToItem(unwrapApiData(res));
}

/** stock_movement.movement_type (0-based) — mirrors backend. */
export const MovementType = {
  STOCK_IN: 0,
  STOCK_OUT: 1,
  TRANSFER: 2,
} as const;

export type MovementTypeCode = (typeof MovementType)[keyof typeof MovementType];

export interface InventoryLocation {
  locationId: string;
  locationCode: string;
  name: string;
  locationType: number;
  branchId: string | null;
}

interface LocationApiRow {
  locationId: string;
  locationCode: string;
  nameI18nKey: string;
  locationType: number;
  branchId: string | null;
}

export interface StockMovement {
  movementId: string;
  movementNo: string;
  movementType: MovementTypeCode;
  movementSubType?: number;
  fromLocationId: string | null;
  toLocationId: string | null;
  memo: string | null;
  movedAt: string;
  reference: string;
  status?: number;
}

interface MovementApiRow {
  movementId: string;
  movementNo: string;
  movementType: number;
  movementSubType?: number;
  fromLocationId: string | null;
  toLocationId: string | null;
  memo: string | null;
  movedAt: string;
  status?: number;
}

export interface MovementLinePayload {
  seqIdx: number;
  productId: string;
  qtyScaled: number;
  unitCostMinor?: number;
  batchNo?: string;
  expiryAt?: string;
  productionAt?: string;
  /** كمية التعبئة (وحدات كبرى — مترابطة دائماً مع convFactorApplied وقت الإدخال). اختياري: لو مستخدم مش بيستخدم التعبئة، مش مطلوبة. */
  packQtyScaled?: number;
  /** معامل التحويل المطبق فعلياً وقت الحركة = qtyScaled / packQtyScaled. يجب ادخاله مع packQtyScaled دائماً (لو واحد موجود التاني لازم موجود). */
  convFactorApplied?: number;
}

export interface AverageUnitCostSnapshot {
  productId: string;
  locationId: string;
  qtyScaled: number;
  unitCostMinor: number;
  totalValueMinor: number;
  costingMethod: "weighted_average";
}

export async function fetchAverageUnitCost(
  productId: string,
  locationId: string,
): Promise<AverageUnitCostSnapshot> {
  const pid = productId.trim();
  const lid = locationId.trim();
  if (!pid || !lid) throw new Error("productId and locationId are required");

  const res = await api.get<
    { ok: true; data: AverageUnitCostSnapshot } | import("./types").ApiErrorBody
  >(
    `/v1/inventory/average-cost?productId=${encodeURIComponent(pid)}&locationId=${encodeURIComponent(lid)}`,
  );

  return unwrapApiData(res);
}

export async function lookupItemByCode(code: string): Promise<InventoryItem> {
  const q = code.trim();
  if (!q) throw new Error("code is required");

  const res = await api.get<
    { ok: true; data: ProductApiRow } | import("./types").ApiErrorBody
  >(`/v1/inventory/items/lookup?code=${encodeURIComponent(q)}`);

  return mapProductToItem(unwrapApiData(res));
}

export interface PostMovementPayload {
  movementType: MovementTypeCode;
  movedAt: string;
  /** Required for Stock In (0) and Stock Out (1). */
  locationId?: string;
  /** Required for Transfer (2) — inter-branch / inter-warehouse source. */
  sourceLocationId?: string;
  /** Required for Transfer (2) — inter-branch / inter-warehouse destination. */
  destinationLocationId?: string;
  lines: MovementLinePayload[];
  /** Stock in: 10 purchase receipt (GRN) or 12 production output (PRO). */
  movementSubType?: number;
  /** Quotation pulled into a stock issue. Does not apply to stock in. */
  salesDocId?: string;
  /** Purchase order received by a stock-in (GRN). */
  purchaseDocId?: string;
  /** Supplier of a purchase receipt (GRN); lets purchasing price the receipt later. */
  partyId?: string;
  memo?: string;
  inventoryAccountId?: string;
  offsetAccountId?: string;
  verticalCode?: number;
}

export interface PostMovementResult {
  movementId: string;
  movementNo: string;
  movementType: MovementTypeCode;
}

export interface CreateLocationPayload {
  locationCode: string;
  name: string;
  locationType?: number;
}

function mapLocation(row: LocationApiRow): InventoryLocation {
  return {
    locationId: row.locationId,
    locationCode: row.locationCode,
    // Template locations come back as an i18n key (e.g. "location.main_warehouse").
    name: i18n.exists(row.nameI18nKey) ? i18n.t(row.nameI18nKey) : row.nameI18nKey,
    locationType: row.locationType,
    branchId: row.branchId,
  };
}

function mapMovement(row: MovementApiRow): StockMovement {
  return {
    movementId: row.movementId,
    movementNo: row.movementNo,
    movementType: row.movementType as MovementTypeCode,
    movementSubType: row.movementSubType,
    fromLocationId: row.fromLocationId,
    toLocationId: row.toLocationId,
    memo: row.memo,
    movedAt: row.movedAt,
    reference: row.movementNo,
    status: row.status,
  };
}

export async function fetchLocations(): Promise<InventoryLocation[]> {
  const res = await api.get<
    { ok: true; data: LocationApiRow[] } | import("./types").ApiErrorBody
  >("/v1/inventory/locations");

  return unwrapApiList(res).map(mapLocation);
}

export const fetchInventoryLocations = fetchLocations;

export async function createLocation(
  payload: CreateLocationPayload,
): Promise<InventoryLocation> {
  const res = await api.post<
    { ok: true; data: LocationApiRow } | import("./types").ApiErrorBody
  >("/v1/inventory/locations", {
    locationCode: payload.locationCode.trim(),
    nameI18nKey: payload.name.trim(),
    locationType: payload.locationType ?? 0,
  });

  return mapLocation(unwrapApiData(res));
}

export async function fetchNextMovementNo(movementSubType: number): Promise<string> {
  const res = await api.get<
    { ok: true; data: { movementNo: string } } | import("./types").ApiErrorBody
  >(`/v1/inventory/movements/next-no?movementSubType=${encodeURIComponent(String(movementSubType))}`);
  return unwrapApiData(res).movementNo;
}

export async function fetchMovements(): Promise<StockMovement[]> {
  const res = await api.get<
    { ok: true; data: MovementApiRow[] } | import("./types").ApiErrorBody
  >("/v1/inventory/movements");

  const rows = unwrapApiList(res).map(mapMovement);

  return [...rows].sort((a, b) => {
    const parse = (value: string) => {
      const m = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/.exec(value.trim());
      if (!m) return 0;
      return new Date(
        Number(m[3]),
        Number(m[2]) - 1,
        Number(m[1]),
        Number(m[4]),
        Number(m[5]),
      ).getTime();
    };
    return parse(b.movedAt) - parse(a.movedAt);
  });
}

/**
 * Posts inventory movement to unified endpoint.
 * Transfer (2) requires sourceLocationId + destinationLocationId; Stock In/Out require locationId.
 */
export async function postMovement(
  payload: PostMovementPayload,
): Promise<PostMovementResult> {
  if (!payload.lines.length) {
    throw new Error("at least one movement line is required");
  }

  const body: Record<string, unknown> = {
    movementType: payload.movementType,
    movedAt: payload.movedAt,
    lines: payload.lines,
    memo: payload.memo?.trim() || undefined,
    verticalCode: payload.verticalCode,
    movementSubType: payload.movementSubType,
    salesDocId: payload.salesDocId,
    purchaseDocId: payload.purchaseDocId || undefined,
    partyId: payload.partyId || undefined,
  };

  switch (payload.movementType) {
    case MovementType.STOCK_IN: {
      if (!payload.locationId) throw new Error("locationId is required for stock in");
      body.locationId = payload.locationId;
      if (payload.inventoryAccountId?.trim()) body.inventoryAccountId = payload.inventoryAccountId.trim();
      if (payload.offsetAccountId?.trim()) body.offsetAccountId = payload.offsetAccountId.trim();
      break;
    }
    case MovementType.STOCK_OUT: {
      if (!payload.locationId) throw new Error("locationId is required for stock out");
      body.locationId = payload.locationId;
      if (payload.inventoryAccountId?.trim()) body.inventoryAccountId = payload.inventoryAccountId.trim();
      if (payload.offsetAccountId?.trim()) body.offsetAccountId = payload.offsetAccountId.trim();
      break;
    }
    case MovementType.TRANSFER: {
      if (!payload.sourceLocationId || !payload.destinationLocationId) {
        throw new Error("sourceLocationId and destinationLocationId are required for transfer");
      }
      if (payload.sourceLocationId === payload.destinationLocationId) {
        throw new Error("source and destination locations must differ");
      }
      body.sourceLocationId = payload.sourceLocationId;
      body.destinationLocationId = payload.destinationLocationId;
      break;
    }
    default:
      throw new Error("invalid movementType");
  }

  const res = await api.post<
    | { ok: true; data: PostMovementResult }
    | import("./types").ApiErrorBody
  >("/v1/inventory/movements", body);

  return unwrapApiData(res);
}

export async function voidInventoryMovement(movementId: string): Promise<{
  movementId: string;
  movementNo: string;
  reversalEntryId: string | null;
}> {
  const id = movementId.trim();
  if (!id) throw new Error("movementId is required");
  const res = await api.post<
    | { ok: true; data: { movementId: string; movementNo: string; reversalEntryId: string | null } }
    | import("./types").ApiErrorBody
  >(`/v1/inventory/movements/${encodeURIComponent(id)}/void`, {});
  return unwrapApiData(res);
}

export const MovementSubType = {
  LEGACY_GENERAL: 0,
  PURCHASE_RECEIPT: 10,
  CUSTOMER_RETURN: 11,
  PRODUCTION_OUTPUT: 12,
  TRANSFER_IN: 13,
  STOCK_ADJUSTMENT_PLUS: 14,
  OPENING_BALANCE: 15,
  OTHER_IN: 19,
  SALES_DELIVERY: 20,
  SUPPLIER_RETURN: 21,
  RAW_MATERIAL_ISSUE: 22,
  TRANSFER_OUT: 23,
  STOCK_ADJUSTMENT_MINUS: 24,
  WRITEOFF: 25,
  SAMPLE_GIFT: 26,
  OTHER_OUT: 29,
  TRANSFER_BETWEEN_WAREHOUSES: 30,
  PRODUCTION_RUN: 31,
} as const;
export type MovementSubTypeCode = (typeof MovementSubType)[keyof typeof MovementSubType];

export interface MovementSubTypeMeta {
  key: string;
  code: MovementSubTypeCode;
  kind: "stock_in" | "stock_out" | "transfer_or_produce" | "legacy";
  requiresParty: boolean;
  prefix: string;
}

export interface ItemTrialBalanceRow {
  productId: string;
  sku: string;
  productName: string;
  productClass: number;
  uomCode: number;
  openingQty: number;
  openingValue: number;
  inQty: number;
  inValue: number;
  outQty: number;
  outValue: number;
  closingQty: number;
  closingValue: number;
  avgUnitCostMinor: number;
  // CLEAN compat aliases: same values, different naming (additive open-shape extensions)
  locationId: string;
  locationName: string;
  uom: string;
  openingQtyScaled: number;
  openingValueMinor: number;
  stockInQtyScaled: number;
  stockInValueMinor: number;
  stockOutQtyScaled: number;
  stockOutValueMinor: number;
  closingQtyScaled: number;
  closingValueMinor: number;
  // Dual-UOM additive display columns: non-zero when product uses pack/conv
  openingPackQtyScaled?: number;
  stockInPackQtyScaled?: number;
  stockOutPackQtyScaled?: number;
  closingPackQtyScaled?: number;
  // Meta info for pack (shown in CSV)
  packUnitName?: string | null;
  stdPackConv?: number | null;
}

export interface ItemCardMovementRow {
  movedAt: string;
  movementNo: string;
  referenceNo: string | null;
  movementSubType: number;
  partyId: string | null;
  locationInId: string | null;
  locationOutId: string | null;
  inQty: number;
  inValueMinor: number;
  outQty: number;
  outValueMinor: number;
  balanceQty: number;
  balanceValue: number;
  avgUnitCostMinor: number;
  memo: string | null;
  // Dual-UOM additive display columns: non-null only for movements entered with pack
  packQtyScaled?: number | null;
  convFactorApplied?: number | null;
  balancePackQtyScaled?: number;
  // ItemCard Report convenience aliases (derived from packQtyScaled + movement direction)
  inPackQtyScaled?: number | null;
  outPackQtyScaled?: number | null;
}
export interface ItemCardResult {
  productMeta: InventoryItem | null;
  openingBalance: { qty: number; value: number };
  movements: ItemCardMovementRow[];
  closingBalance: { qty: number; value: number };
  // Dual-UOM additive opening/closing pack balances (parallel to existing qty/value)
  openingPackBalance?: number;
  closingPackBalance?: number;
  branchId?: string | null;
  salespersonId?: string | null;
  filterNote?: string | null;
}

export interface ValuationByLocationRow {
  locationId: string;
  locationCode: string;
  locationName: string;
  totalQty: number;
  totalValueMinor: number;
  distinctProducts: number;
}

export interface ExpiryReportRow {
  productId: string;
  sku: string;
  productName: string;
  locationId: string;
  locationName: string;
  batchNo: string | null;
  productionAt: string | null;
  expiryAt: string | null;
  remainingQty: number;
  unitCostMinor: number;
  valueMinor: number;
  status: "expired" | "within_30d" | "within_60d" | "ok";
  // CLEAN compat aliases
  productionDate: string | null;
  expiryDate: string | null;
  qtyScaled: number;
  uom: string;
}

export interface ExpiryReportResult {
  rows: ExpiryReportRow[];
  totals: {
    count: number;
    expiredCount: number;
    soonCount: number;
    totalValue: number;
  };
}

export interface InventoryKpisSnapshot {
  totalInventoryValueMinor: number;
  activeProducts: number;
  totalProducts: number;
  lowStockCount: number;
  expiredBatchCount: number;
  expiringSoon30dCount: number;
  cogs30dValueMinor: number;
  stockIn30dValueMinor: number;
  stockOut30dValueMinor: number;
  byLocation: ValuationByLocationRow[];
  topProductsByValue: Array<{
    productId: string;
    sku: string;
    name: string;
    valueMinor: number;
  }>;
}

export const ANOMALY_SEVERITY = {
  INFO: 0,
  WARNING: 1,
  CRITICAL: 2,
} as const;

export type AnomalySeverityCode = (typeof ANOMALY_SEVERITY)[keyof typeof ANOMALY_SEVERITY];

export interface InventoryAnomalyFlag {
  flagId: string;
  severity: AnomalySeverityCode;
  entityType: "stock_layer" | "stock_movement" | "product";
  entityId: string;
  messageI18nKey: string;
  detectedAt: string;
  resolvedAt: string | null;
}

export interface AnomalyDetectionResult {
  total: number;
  bySeverity: {
    critical: number;
    warning: number;
    info: number;
  };
}

export async function fetchMovementSubTypes(): Promise<MovementSubTypeMeta[]> {
  const res = await api.get<
    { ok: true; data: MovementSubTypeMeta[] } | import("./types").ApiErrorBody
  >("/v1/inventory/movement-subtypes");
  return unwrapApiList(res);
}

export interface ItemBalanceQuery {
  fromDate?: string;
  toDate?: string;
  productId?: string;
  locationId?: string;
  branchId?: string;
  includeZeroBalance?: boolean;
}
export async function fetchItemTrialBalance(
  q: ItemBalanceQuery = {},
): Promise<{ rows: ItemTrialBalanceRow[]; totals: { openingValueMinor: number; inValueMinor: number; outValueMinor: number; closingValueMinor: number } }> {
  const params = new URLSearchParams();
  if (q.fromDate) params.set("fromDate", q.fromDate);
  if (q.toDate) params.set("toDate", q.toDate);
  if (q.productId) params.set("productId", q.productId);
  if (q.locationId) params.set("locationId", q.locationId);
  if (q.branchId?.trim()) params.set("branchId", q.branchId.trim());
  if (q.includeZeroBalance) params.set("includeZeroBalance", "1");
  const url = params.toString()
    ? `/v1/inventory/reports/item-balance?${params.toString()}`
    : "/v1/inventory/reports/item-balance";
  const res = await api.get<
    | {
        ok: true;
        data: ItemTrialBalanceRow[];
        meta: {
          totals: {
            openingValueMinor: number;
            inValueMinor: number;
            outValueMinor: number;
            closingValueMinor: number;
          };
          count: number;
        };
      }
    | import("./types").ApiErrorBody
  >(url);
  const unwrapped = res as unknown as { ok: true; data: ItemTrialBalanceRow[]; meta: { totals: any; count: number } };
  if (!("ok" in unwrapped) || unwrapped.ok !== true) {
    const err = unwrapped as unknown as import("./types").ApiErrorBody;
    throw new Error(err.error?.message || "item balance failed");
  }
  return { rows: unwrapped.data, totals: unwrapped.meta.totals };
}

export interface InventoryMovementSummaryRow {
  moveType: string;
  movementSubType: number;
  qtyScaled: number;
  valueMinor: number;
  lineCount: number;
  avgUnitMinor: number;
}

export interface InventoryMovementSummaryReport {
  rows: InventoryMovementSummaryRow[];
  totalQtyScaled: number;
  totalValueMinor: number;
  hideMoney: boolean;
  note: string;
}

export async function fetchInventoryMovementSummary(q: {
  startDate: string;
  endDate: string;
  branchId?: string;
}): Promise<InventoryMovementSummaryReport> {
  const params = new URLSearchParams({
    startDate: q.startDate,
    endDate: q.endDate,
  });
  if (q.branchId?.trim()) params.set("branchId", q.branchId.trim());
  const res = await api.get<
    | { ok: true; data: InventoryMovementSummaryReport }
    | import("./types").ApiErrorBody
  >(`/v1/inventory/reports/movement-summary?${params}`);
  if (!("ok" in res) || res.ok !== true) {
    const err = res as import("./types").ApiErrorBody;
    throw new Error(err.error?.message || "movement summary failed");
  }
  return res.data;
}

export interface SlowMovingStockRow {
  productId: string;
  sku: string;
  displayName: string;
  qtyScaled: number;
  valueMinor: number;
  daysSince: number;
  lastMoveAt: string | null;
}

export interface SlowMovingStockReport {
  asOfDate: string;
  staleDays: number;
  rows: SlowMovingStockRow[];
  note: string;
}

export async function fetchSlowMovingStockReport(q: {
  asOfDate: string;
  staleDays?: number;
  locationId?: string;
  branchId?: string;
}): Promise<SlowMovingStockReport> {
  const params = new URLSearchParams({ asOfDate: q.asOfDate });
  if (q.staleDays != null) params.set("staleDays", String(q.staleDays));
  if (q.locationId) params.set("locationId", q.locationId);
  if (q.branchId?.trim()) params.set("branchId", q.branchId.trim());
  const res = await api.get<
    | { ok: true; data: SlowMovingStockReport }
    | import("./types").ApiErrorBody
  >(`/v1/inventory/reports/slow-moving?${params}`);
  if (!("ok" in res) || res.ok !== true) {
    const err = res as import("./types").ApiErrorBody;
    throw new Error(err.error?.message || "slow moving stock failed");
  }
  return res.data;
}

export async function fetchItemCard(
  productId: string,
  q: {
    fromDate?: string;
    toDate?: string;
    locationId?: string;
    branchId?: string;
    salespersonId?: string;
  } = {},
): Promise<ItemCardResult> {
  const pid = productId.trim();
  if (!pid) throw new Error("productId required");
  const params = new URLSearchParams();
  if (q.fromDate) params.set("fromDate", q.fromDate);
  if (q.toDate) params.set("toDate", q.toDate);
  if (q.locationId) params.set("locationId", q.locationId);
  if (q.branchId?.trim()) params.set("branchId", q.branchId.trim());
  if (q.salespersonId?.trim()) params.set("salespersonId", q.salespersonId.trim());
  const url = params.toString()
    ? `/v1/inventory/reports/item-card/${encodeURIComponent(pid)}?${params.toString()}`
    : `/v1/inventory/reports/item-card/${encodeURIComponent(pid)}`;
  const res = await api.get<
    { ok: true; data: ItemCardResult } | import("./types").ApiErrorBody
  >(url);
  return unwrapApiData(res);
}

export async function fetchInventoryValuationByLocation(): Promise<{
  rows: ValuationByLocationRow[];
  grandTotalValueMinor: number;
}> {
  const res = await api.get<
    | { ok: true; data: ValuationByLocationRow[]; meta: { grandTotalValueMinor: number } }
    | import("./types").ApiErrorBody
  >("/v1/inventory/reports/valuation");
  const unwrapped = res as unknown as {
    ok: true;
    data: ValuationByLocationRow[];
    meta: { grandTotalValueMinor: number };
  };
  if (!("ok" in unwrapped) || unwrapped.ok !== true) {
    const err = unwrapped as unknown as import("./types").ApiErrorBody;
    throw new Error(err.error?.message || "valuation failed");
  }
  return { rows: unwrapped.data, grandTotalValueMinor: unwrapped.meta.grandTotalValueMinor };
}

export async function fetchExpiryReport(
  q: {
    scope?: "expired" | "30d" | "60d" | "custom";
    fromDate?: string;
    toDate?: string;
    locationId?: string;
    status?: "all" | "expired" | "within_30d" | "within_60d" | "ok";
    includeZeroQty?: boolean;
  } = {},
): Promise<{ rows: ExpiryReportRow[]; totals: { expiredCount: number; soonCount: number; totalValue: number; count: number } }> {
  const params = new URLSearchParams();
  if (q.scope) params.set("scope", q.scope);
  if (q.fromDate) params.set("fromDate", q.fromDate);
  if (q.toDate) params.set("toDate", q.toDate);
  if (q.locationId) params.set("locationId", q.locationId);
  if (q.status) params.set("status", q.status);
  if (q.includeZeroQty) params.set("includeZeroQty", "1");
  const url = params.toString()
    ? `/v1/inventory/reports/expiry?${params.toString()}`
    : "/v1/inventory/reports/expiry";
  const res = await api.get<
    | {
        ok: true;
        data: ExpiryReportRow[];
        meta: { totals: { expiredCount: number; soonCount: number; totalValue: number }; count: number };
      }
    | import("./types").ApiErrorBody
  >(url);
  const unwrapped = res as unknown as {
    ok: true;
    data: ExpiryReportRow[];
    meta: { totals: any; count: number };
  };
  if (!("ok" in unwrapped) || unwrapped.ok !== true) {
    const err = unwrapped as unknown as import("./types").ApiErrorBody;
    throw new Error(err.error?.message || "expiry report failed");
  }
  return { rows: unwrapped.data, totals: unwrapped.meta.totals };
}

export async function fetchInventoryKpisSnapshot(): Promise<InventoryKpisSnapshot> {
  const res = await api.get<
    { ok: true; data: InventoryKpisSnapshot } | import("./types").ApiErrorBody
  >("/v1/inventory/kpis/snapshot");
  return unwrapApiData(res);
}

export async function runInventoryAnomalyDetection(opts?: { truncate?: boolean }): Promise<AnomalyDetectionResult> {
  const res = await api.post<
    { ok: true; data: AnomalyDetectionResult } | import("./types").ApiErrorBody
  >("/v1/inventory/anomalies/detect", { truncate: opts?.truncate === true });
  return unwrapApiData(res);
}

export async function fetchInventoryAnomalies(params?: {
  severity?: "all" | AnomalySeverityCode;
  includeResolved?: boolean;
}): Promise<InventoryAnomalyFlag[]> {
  const q: string[] = [];
  if (params?.severity !== undefined) q.push(`severity=${encodeURIComponent(String(params.severity))}`);
  if (params?.includeResolved) q.push("resolved=1");
  const url = `/v1/inventory/anomalies${q.length ? "?" + q.join("&") : ""}`;
  const res = await api.get<
    { ok: true; data: InventoryAnomalyFlag[] } | import("./types").ApiErrorBody
  >(url);
  return unwrapApiData(res);
}

export type ReorderKind = "purchase" | "production";

export interface ReorderRow {
  productId: string;
  sku: string;
  nameI18nKey: string;
  nameCustom: string | null;
  productClass: number;
  pointScaled: number | null;
  onHandScaled: number;
  onOrderScaled: number;
  belowPoint: boolean;
  suggestedQtyScaled: number;
  /** null when the caller may not see prices (e.g. storekeepers). */
  lastCostMinor: number | null;
}

/** Items under their purchase reorder point (raw / trading) or production point (finished / semi). */
export interface ReorderReportMeta extends Record<string, unknown> {
  kind: ReorderKind;
  count: number;
  canSeePrices?: boolean;
  branchId?: string | null;
  filterNote?: string | null;
}

export async function fetchReorderReport(
  kind: ReorderKind,
  belowOnly = true,
  branchId?: string,
): Promise<{ rows: ReorderRow[]; meta: ReorderReportMeta }> {
  const qs = new URLSearchParams({
    kind,
    belowOnly: belowOnly ? "1" : "0",
  });
  if (branchId?.trim()) qs.set("branchId", branchId.trim());
  const res = await api.get<
    { ok: true; data: ReorderRow[]; meta: ReorderReportMeta } | ApiErrorBody
  >(`/v1/inventory/reorder-report?${qs.toString()}`);
  if (isApiError(res)) {
    throw new ApiClientError(res.error.message, 400, res.error.code, res.error.reason);
  }
  return { rows: res.data, meta: res.meta };
}

export async function saveItemPoints(
  productId: string,
  points: { reorderPointScaled?: number | null; productionPointScaled?: number | null },
): Promise<void> {
  await api.patch<unknown>(`/v1/inventory/items/${encodeURIComponent(productId)}/points`, points);
}

/** Edit an item's classification (category / colour / size / base name); the full name is recomputed server-side. */
export async function saveItemClassification(
  productId: string,
  input: {
    categoryId?: string | null;
    colorId?: string | null;
    sizeId?: string | null;
    baseName?: string;
  },
): Promise<InventoryItem> {
  const res = await api.patch<
    { ok: true; data: ProductApiRow } | import("./types").ApiErrorBody
  >(`/v1/inventory/items/${encodeURIComponent(productId)}`, input);
  return mapProductToItem(unwrapApiData(res));
}

/** Update core item fields from directories / quick edit. */
export async function patchInventoryItem(
  productId: string,
  input: {
    sku?: string;
    baseName?: string;
    listPriceMinor?: number;
    productClass?: number;
  },
): Promise<InventoryItem> {
  const body: Record<string, unknown> = {};
  if (input.sku !== undefined) body.sku = input.sku;
  if (input.baseName !== undefined) body.baseName = input.baseName;
  if (input.listPriceMinor !== undefined) body.listPriceMinor = input.listPriceMinor;
  if (input.productClass !== undefined) body.productClass = input.productClass;
  const res = await api.patch<
    { ok: true; data: ProductApiRow } | import("./types").ApiErrorBody
  >(`/v1/inventory/items/${encodeURIComponent(productId)}`, body);
  return mapProductToItem(unwrapApiData(res));
}
